import {
  MailOutlined,
  MobileOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  ModalForm,
  ProFormDatePicker,
  ProFormDigit,
  ProFormField,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Tooltip,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { parseCrmRequestError } from '@/modules/zy-crm/components/business-settings/common';
import { CrmTagSelector } from '@/modules/zy-crm/components/crm-tags';
import { useDuplicateNameCheck } from '@/modules/zy-crm/components/duplicate-name-check';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import {
  queryAllTargetReferrals,
  type ReferralFormItem,
  ReferralFormSection,
  syncTargetReferrals,
} from '@/modules/zy-crm/components/referral-form-section';
import {
  ResourceAreaSelector,
  type ResourceAreaValue,
} from '@/modules/zy-crm/components/resource-area-selector';
import {
  ensureChatModelReady,
  getCrmErrorCode,
  showAiModelConfigDialog,
} from '@/modules/zy-crm/utils/ai-model-readiness';
import {
  FOLLOW_UP_STATUS_OPTIONS,
  LEAD_GRADE_OPTIONS,
} from '@/modules/zy-crm/utils/constants';
import type { CrmPageScope } from '@/modules/zy-crm/utils/data-scope';
import { toApiDateTime } from '@/modules/zy-crm/utils/display';
import type { LeadScopedViewMode } from '@/modules/zy-crm/utils/lead-view';
import { createSelectedMemberAwareAdapter } from '@/modules/zy-crm/utils/member-picker-adapter';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  fetchContactOptions,
  fetchCustomerLevelOptions,
  fetchCustomerOptions,
  fetchSourceChannelOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import {
  createLead,
  createLeadSmartDraft,
  queryLeadDetail,
  updateLead,
} from '@/services/bff/lead.api';
import { replaceEntityTags } from '@/services/bff/taxonomy-management.api';
import type {
  CreateLeadDto,
  LeadItem,
  UpdateLeadDto,
} from '@/services/bff/types/lead';
import type { ReferralItem } from '@/services/bff/types/referral';
import { queryTaxonomyTreeByIdentifier } from '@/services/mp/taxonomy';
import { useOpenPlatformConfig } from '@/services/openPlatformToken';
import { getCurrentMemberUuid } from '@/utils/currentMember';
import {
  type AreaCascaderOption,
  type AreaUuidPathMap,
  buildAreaCascaderData,
  ensureAreaCascaderFallback,
  flattenTaxonomyToOptions,
  type TaxonomyOption,
} from '@/utils/taxonomy';

const TAXONOMY_AREA = 'CnAdministrativeDivision';
const TAXONOMY_INDUSTRY = 'TonghuashunIndustryTaxonomy';

export type LeadFormValues = {
  name: string;
  companyName?: string;
  contactName?: string;
  website?: string;
  employeeCount?: number;
  annualRevenue?: number;
  followUpStatus: number;
  contactLastTime?: Dayjs;
  contactLastContent?: string;
  contactNextTime?: Dayjs;
  nextAction?: string;
  ownerUserUuid: string;
  transformStatus: boolean;
  customerUuid?: string;
  contactUuid?: string;
  mobile?: string;
  telephone?: string;
  qq?: string;
  wechat?: string;
  email?: string;
  areas?: ResourceAreaValue[];
  detailAddress?: string;
  industryUuid?: string;
  customerLevel?: string;
  sourceChannel?: string | number;
  leadAmount?: number;
  manualLeadGrade?: 'A' | 'B' | 'C' | 'D';
  description?: string;
  customTags?: string[];
  tagUuids?: string[];
  referrals?: ReferralFormItem[];
};

type LeadEditModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: number | string;
  leadUuid?: string;
  smartInput?: boolean;
  initialValues?: Partial<LeadFormValues>;
  defaultCustomerUuid?: string;
  defaultCustomerName?: string;
  currentMemberUuid?: string | null;
  pageScope?: CrmPageScope;
  leadViewMode?: LeadScopedViewMode;
  /** Called after each successful creation, including save-and-continue. */
  onCreated?: (record: LeadItem) => void;
  onSuccess: (record?: LeadItem) => void;
};

const compactFieldStyle = { width: '100%' };
const DEFAULT_LEAD_MODAL_WIDTH = getResponsiveModalWidth('wideForm');
const SMART_PANEL_WIDTH = 320;
const SMART_INPUT_PLACEHOLDER =
  '请输入线索来源、客户/项目名称、机会背景、联系人、联系方式、预算或下一步计划等信息';
const SMART_INPUT_TEMPLATES = [
  {
    label: '项目机会',
    value:
      '线索来源：\n客户/项目：\n机会背景：\n关键联系人：\n联系方式：\n预算/金额：\n下一步计划：',
  },
  {
    label: '客户拜访',
    value:
      '拜访对象：\n客户需求：\n当前痛点：\n意向产品/方案：\n联系人及电话：\n预计跟进时间：',
  },
  {
    label: '渠道推荐',
    value:
      '推荐来源：\n被推荐客户/项目：\n推荐原因：\n客户需求：\n联系人信息：\n备注：',
  },
];

const toDateValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const toBoolean = (value: unknown): boolean => {
  if (value instanceof Uint8Array) {
    return value.length > 0 && value[0] === 1;
  }
  if (
    value &&
    typeof value === 'object' &&
    (value as { type?: string }).type === 'Buffer'
  ) {
    const data = (value as { data?: unknown }).data;
    if (Array.isArray(data) && data.length > 0) {
      return Number(data[0]) === 1;
    }
  }
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    if (value.length === 1) {
      const code = value.charCodeAt(0);
      if (code === 1) return true;
      if (code === 0) return false;
    }
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off', ''].includes(normalized)) return false;
    return Boolean(value);
  }
  if (Array.isArray(value) && value.length > 0) {
    return toBoolean(value[0]);
  }
  return false;
};

const normalizeText = (value: unknown) => String(value || '').trim();

const ensureCurrentOption = (
  options: RelationOption[],
  value: unknown,
  fallbackLabel?: string,
) => {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return options;
  }
  if (options.some((item) => normalizeText(item.value) === normalizedValue)) {
    return options;
  }
  return [
    {
      value: normalizedValue,
      label: normalizeText(fallbackLabel) || normalizedValue,
    },
    ...options,
  ];
};

const toFormValues = (
  record?: LeadItem | null,
  currentMemberUuid?: string | null,
  defaultCustomerUuid?: string,
): Partial<LeadFormValues> => ({
  name: record?.name,
  companyName: record?.companyName,
  contactName: record?.contactName,
  website: record?.website,
  employeeCount: record?.employeeCount,
  annualRevenue: record?.annualRevenue,
  followUpStatus: record?.followUpStatus ?? 1,
  contactLastTime: toDateValue(record?.contactLastTime),
  contactLastContent: record?.contactLastContent,
  contactNextTime: toDateValue(record?.contactNextTime),
  nextAction: record?.nextAction,
  ownerUserUuid:
    record?.ownerUserUuid ||
    currentMemberUuid ||
    getCurrentMemberUuid() ||
    undefined,
  transformStatus: toBoolean(record?.transformStatus),
  customerUuid: record?.customerUuid || defaultCustomerUuid,
  contactUuid: record?.contactUuid || undefined,
  mobile: record?.mobile,
  telephone: record?.telephone,
  qq: record?.qq,
  wechat: record?.wechat,
  email: record?.email,
  areas: record?.areas,
  detailAddress: record?.detailAddress,
  industryUuid: record?.industryUuid,
  customerLevel:
    record?.customerLevel === undefined || record?.customerLevel === null
      ? undefined
      : String(record.customerLevel),
  sourceChannel: record?.sourceChannel,
  leadAmount: record?.leadAmount,
  manualLeadGrade: ['A', 'B', 'C', 'D'].includes(
    String(record?.manualLeadGrade || ''),
  )
    ? (record?.manualLeadGrade as 'A' | 'B' | 'C' | 'D')
    : undefined,
  description: record?.description,
  customTags: record?.customTags || [],
  tagUuids: (
    (record as LeadItem & { tags?: Array<{ uuid: string }> })?.tags || []
  ).map((tag) => tag.uuid),
});

const normalizePayload = (values: LeadFormValues): Partial<CreateLeadDto> => {
  const payload: Record<string, unknown> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (key === 'referrals' || key === 'tagUuids' || key === 'customTags') {
      return;
    }
    if (key === 'manualLeadGrade') {
      payload.manualLeadGrade = value || null;
      return;
    }
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (dayjs.isDayjs(value)) {
      payload[key] = toApiDateTime(value);
      return;
    }
    payload[key] = value;
  });
  payload.followUpStatus = Number(values.followUpStatus ?? 1);
  // ProForm omits cleared fields from values; explicitly persist cleared relations.
  payload.customerUuid = values.customerUuid || null;
  payload.contactUuid = values.contactUuid || null;
  payload.transformStatus =
    typeof values.transformStatus === 'boolean'
      ? values.transformStatus
      : toBoolean(values.transformStatus);
  return payload as Partial<CreateLeadDto>;
};

const toDraftFormValues = (
  values: Partial<CreateLeadDto> | undefined,
): Partial<LeadFormValues> => {
  if (!values) {
    return {};
  }
  return {
    ...values,
    contactLastTime: toDateValue(values.contactLastTime),
    contactNextTime: toDateValue(values.contactNextTime),
    customerLevel:
      values.customerLevel === undefined || values.customerLevel === null
        ? undefined
        : String(values.customerLevel),
  } as Partial<LeadFormValues>;
};

export function LeadEditModal(props: LeadEditModalProps) {
  const openPlatformConfig = useOpenPlatformConfig();
  const {
    open,
    onOpenChange,
    width = DEFAULT_LEAD_MODAL_WIDTH,
    leadUuid,
    smartInput = false,
    initialValues,
    defaultCustomerUuid,
    defaultCustomerName,
    currentMemberUuid,
    pageScope = 'self',
    leadViewMode,
    onCreated,
    onSuccess,
  } = props;
  const [form] = Form.useForm<LeadFormValues>();
  const duplicateCheck = useDuplicateNameCheck({
    entity: 'lead',
    form,
    enabled: open && !leadUuid,
  });
  const selectedCustomerUuid = Form.useWatch('customerUuid', form);
  const saveAndContinueRef = useRef(false);
  const [editingRecord, setEditingRecord] = useState<LeadItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = useState(false);
  const [areaOptions, setAreaOptions] = useState<AreaCascaderOption[]>([]);
  const [areaUuidPathMap, setAreaUuidPathMap] = useState<AreaUuidPathMap>({});
  const [industryOptions, setIndustryOptions] = useState<TaxonomyOption[]>([]);
  const [customerLevelOptions, setCustomerLevelOptions] = useState<
    RelationOption[]
  >([]);
  const [sourceChannelOptions, setSourceChannelOptions] = useState<
    RelationOption[]
  >([]);
  const [initialReferrals, setInitialReferrals] = useState<ReferralItem[]>([]);
  const [relationLoading, setRelationLoading] = useState(false);
  const [smartText, setSmartText] = useState('');
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartNotes, setSmartNotes] = useState<string[]>([]);
  const prefilledOwnerUuid =
    editingRecord?.ownerUserUuid ||
    currentMemberUuid ||
    getCurrentMemberUuid() ||
    undefined;
  const ownerMemberAdapter = useMemo(
    () =>
      createSelectedMemberAwareAdapter(orgDirectoryAdapter, prefilledOwnerUuid),
    [prefilledOwnerUuid],
  );

  const loadRelationOptions = useCallback(
    async (keyword?: string) => {
      setRelationLoading(true);
      try {
        const [areaTree, industryTree, sourceChannels, customerLevels] =
          await Promise.all([
            queryTaxonomyTreeByIdentifier(TAXONOMY_AREA),
            queryTaxonomyTreeByIdentifier(TAXONOMY_INDUSTRY),
            fetchSourceChannelOptions(keyword),
            fetchCustomerLevelOptions(keyword),
          ]);

        const areaTreeData = ensureAreaCascaderFallback(
          buildAreaCascaderData(areaTree.tags),
          String(
            editingRecord?.primaryAreaUuid ||
              form.getFieldValue('areas')?.[0]?.areaUuid ||
              '',
          ).trim(),
          editingRecord?.primaryAreaName,
        );
        const flatIndustries = flattenTaxonomyToOptions(industryTree.tags);

        setAreaOptions(areaTreeData.options);
        setAreaUuidPathMap(areaTreeData.uuidPathMap);

        const resolvedIndustryOptions = [...flatIndustries];
        const currentIndustryUuid = editingRecord?.industryUuid;
        const currentIndustryName = editingRecord?.industryName;
        if (
          currentIndustryUuid &&
          !resolvedIndustryOptions.find((o) => o.value === currentIndustryUuid)
        ) {
          resolvedIndustryOptions.unshift({
            value: currentIndustryUuid,
            label: currentIndustryName || currentIndustryUuid,
          });
        }
        setIndustryOptions(resolvedIndustryOptions);
        setCustomerLevelOptions(
          ensureCurrentOption(
            customerLevels,
            editingRecord?.customerLevel,
            typeof editingRecord?.customerLevel === 'number'
              ? `级别${editingRecord.customerLevel}`
              : undefined,
          ),
        );
        setSourceChannelOptions(
          ensureCurrentOption(
            sourceChannels,
            editingRecord?.sourceChannel,
            undefined,
          ),
        );
      } catch (err) {
        console.error('Failed to load taxonomy options:', err);
      } finally {
        setRelationLoading(false);
      }
    },
    [
      editingRecord?.primaryAreaName,
      editingRecord?.primaryAreaUuid,
      editingRecord?.industryName,
      editingRecord?.industryUuid,
      editingRecord?.customerLevel,
      editingRecord?.sourceChannel,
      form,
    ],
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setEditingRecord(null);
      setDetailLoading(false);
      setDetailLoadFailed(false);
      setInitialReferrals([]);
      setSmartText('');
      setSmartNotes([]);
      return;
    }
    let disposed = false;

    const loadRecord = async () => {
      if (!leadUuid) {
        setEditingRecord(null);
        setInitialReferrals([]);
        setDetailLoading(false);
        setDetailLoadFailed(false);
        return;
      }
      setDetailLoading(true);
      setDetailLoadFailed(false);
      try {
        const [record, referrals] = await Promise.all([
          queryLeadDetail(leadUuid, {
            scope: pageScope,
            viewMode: leadViewMode,
          }),
          queryAllTargetReferrals({
            targetType: 'lead',
            targetUuid: leadUuid,
          }),
        ]);
        if (disposed) {
          return;
        }
        setEditingRecord(record);
        setInitialReferrals(referrals);
        setDetailLoadFailed(false);
      } catch (error) {
        if (disposed) {
          return;
        }
        console.error('Failed to load lead detail:', error);
        setDetailLoadFailed(true);
        message.error('线索详情加载失败，请稍后重试');
      } finally {
        if (!disposed) {
          setDetailLoading(false);
        }
      }
    };

    void loadRecord();
    return () => {
      disposed = true;
    };
  }, [form, leadUuid, leadViewMode, open, pageScope]);

  useEffect(() => {
    if (
      !open ||
      detailLoading ||
      detailLoadFailed ||
      (leadUuid && !editingRecord)
    ) {
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      ...toFormValues(editingRecord, currentMemberUuid, defaultCustomerUuid),
      ...(leadUuid ? {} : initialValues || {}),
    });
    void loadRelationOptions();
  }, [
    currentMemberUuid,
    detailLoadFailed,
    detailLoading,
    editingRecord,
    form,
    initialReferrals,
    initialValues,
    leadUuid,
    loadRelationOptions,
    defaultCustomerUuid,
    open,
  ]);

  const handleSmartDraft = async () => {
    const text = smartText.trim();
    if (!text) {
      message.warning('请先输入线索内容');
      return;
    }
    try {
      const ready = await ensureChatModelReady();
      if (!ready) {
        return;
      }
      setSmartLoading(true);
      const result = await createLeadSmartDraft({
        text,
        ownerUserUuid:
          (form.getFieldValue('ownerUserUuid') as string | undefined) ||
          currentMemberUuid ||
          getCurrentMemberUuid() ||
          undefined,
      });
      form.setFieldsValue({
        ...toDraftFormValues(result.values),
        ownerUserUuid:
          result.values?.ownerUserUuid ||
          form.getFieldValue('ownerUserUuid') ||
          currentMemberUuid ||
          getCurrentMemberUuid() ||
          undefined,
      });
      setSmartNotes(result.notes || []);
      message.success('已整理为线索草稿');
    } catch (error) {
      if (getCrmErrorCode(error) === 'CRM_AI_MODEL_NOT_BOUND') {
        showAiModelConfigDialog();
        return;
      }
      message.error(parseCrmRequestError(error, '智能整理失败，请稍后重试'));
    } finally {
      setSmartLoading(false);
    }
  };

  return (
    <ModalForm<LeadFormValues>
      title={leadUuid ? '编辑线索' : smartInput ? '智能录入线索' : '新建线索'}
      width={width}
      layout="vertical"
      form={form}
      open={open}
      modalProps={{
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        className: `${modalStyles.modal} ${modalStyles.form}`,
        centered: true,
        destroyOnHidden: true,
        forceRender: true,
        onCancel: () => onOpenChange(false),
        styles: {
          body: {
            overflowY: 'auto',
            minWidth: 0,
          },
        },
      }}
      onOpenChange={onOpenChange}
      loading={duplicateCheck.busy}
      submitter={{
        searchConfig: { submitText: '保存' },
        submitButtonProps: {
          disabled: duplicateCheck.busy || detailLoadFailed,
          onClick: () => {
            saveAndContinueRef.current = false;
          },
        },
        render: (_, dom) => [
          dom[0],
          !leadUuid ? (
            <Button
              key="save-and-continue"
              disabled={duplicateCheck.busy || detailLoadFailed}
              onClick={() => {
                saveAndContinueRef.current = true;
                form.submit();
              }}
            >
              保存并继续新增
            </Button>
          ) : null,
          dom[1],
        ],
      }}
      onFinishFailed={() => {
        saveAndContinueRef.current = false;
      }}
      onFinish={(values) =>
        duplicateCheck.submit(values, async () => {
          if (detailLoadFailed) {
            message.error('线索详情加载失败，无法提交，请先重试');
            return false;
          }
          const payload = normalizePayload(values);
          let savedRecord: LeadItem;
          const effectiveLeadUuid = leadUuid;
          if (effectiveLeadUuid) {
            savedRecord = await updateLead(
              effectiveLeadUuid,
              payload as UpdateLeadDto,
            );
          } else {
            savedRecord = await createLead(payload as CreateLeadDto);
          }
          await syncTargetReferrals({
            targetType: 'lead',
            targetUuid: savedRecord.uuid,
            values: form.getFieldValue('referrals') || values.referrals,
            initialItems: initialReferrals,
          });
          try {
            await replaceEntityTags(
              'lead',
              savedRecord.uuid,
              values.tagUuids || [],
            );
          } catch {
            message.warning('线索主体已保存、标签未保存，请重新打开后重试');
          }
          if (!leadUuid) onCreated?.(savedRecord);
          message.success(leadUuid ? '更新成功' : '创建成功');
          if (!leadUuid && saveAndContinueRef.current) {
            saveAndContinueRef.current = false;
            form.resetFields();
            form.setFieldsValue(
              toFormValues(null, currentMemberUuid, defaultCustomerUuid),
            );
            setInitialReferrals([]);
            return false;
          }
          onSuccess(savedRecord);
          return true;
        })
      }
    >
      <div
        style={
          smartInput
            ? {
                display: 'flex',
                flexWrap: 'wrap',
                gap: 16,
                alignItems: 'flex-start',
              }
            : undefined
        }
      >
        {smartInput ? (
          <div
            style={{
              flex: `0 0 ${SMART_PANEL_WIDTH}px`,
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
            }}
          >
            <div style={{ marginBottom: 8 }}>线索内容</div>
            <Select
              allowClear
              options={SMART_INPUT_TEMPLATES}
              placeholder="选择常用模板"
              style={{ width: '100%', marginBottom: 12 }}
              onChange={(value) => {
                if (value) {
                  setSmartText(value);
                }
              }}
            />
            <div style={{ marginBottom: 30 }}>
              <Input.TextArea
                value={smartText}
                onChange={(event) => setSmartText(event.target.value)}
                rows={12}
                maxLength={2000}
                showCount
                placeholder={SMART_INPUT_PLACEHOLDER}
              />
            </div>
            <Button
              type="primary"
              block
              icon={<ThunderboltOutlined />}
              loading={smartLoading}
              onClick={handleSmartDraft}
            >
              智能整理
            </Button>
            {smartNotes.length ? (
              <div
                style={{
                  marginTop: 12,
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                {smartNotes.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className={modalStyles.formGrid}
          style={smartInput ? { flex: 1, minWidth: 0 } : undefined}
        >
          <ProFormText name="transformStatus" hidden initialValue={false} />

          <FormSectionDivider title="基本信息" />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormText
                name="name"
                label="标题"
                rules={[{ required: true, message: '请输入标题' }]}
              />
              {duplicateCheck.hint}
            </Col>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormText
                name="companyName"
                label={
                  <Space size={8}>
                    <span>公司名称</span>
                    <Tooltip title="当前项目暂无工商信息数据源">
                      <Button
                        type="link"
                        size="small"
                        icon={<ThunderboltOutlined />}
                        onClick={() =>
                          message.info('当前项目暂无工商信息数据源')
                        }
                        style={{ padding: 0, height: 'auto' }}
                      >
                        工商信息
                      </Button>
                    </Tooltip>
                  </Space>
                }
                fieldProps={{ suffix: <SearchOutlined /> }}
              />
            </Col>
          </Row>
          <Row gutter={[12, 0]}>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormText
                name="website"
                label="网址"
                fieldProps={{ placeholder: 'https://example.com' }}
              />
            </Col>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormField
                name="ownerUserUuid"
                label="负责人"
                rules={[{ required: true, message: '请选择负责人' }]}
                valueType="text"
              >
                <MemberPicker
                  token={openPlatformConfig?.m2mToken || ''}
                  value={
                    (form.getFieldValue('ownerUserUuid') as
                      | string
                      | undefined) || null
                  }
                  onChange={(next) => {
                    form.setFieldValue('ownerUserUuid', next || undefined);
                  }}
                  placeholder="请选择负责人"
                  disabled={detailLoading || detailLoadFailed}
                />
              </ProFormField>
            </Col>
          </Row>
          <ProFormTextArea
            name="description"
            label="描述"
            fieldProps={{
              rows: 3,
              maxLength: 800,
              showCount: true,
              placeholder: '请输入线索描述',
            }}
          />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormSelect
                name="customerLevel"
                label="客户级别"
                fieldProps={{
                  style: compactFieldStyle,
                  showSearch: true,
                  filterOption: (input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                  optionFilterProp: 'label',
                  options: customerLevelOptions,
                  loading: relationLoading,
                  allowClear: true,
                  placeholder: '请选择',
                }}
              />
            </Col>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <Form.Item name="tagUuids" label="标签">
                <CrmTagSelector entityType="lead" canCreate />
              </Form.Item>
            </Col>
          </Row>

          <FormSectionDivider title="关联信息" />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormSelect
                name="customerUuid"
                label="关联客户"
                showSearch
                debounceTime={300}
                params={{
                  currentUuid:
                    editingRecord?.customerUuid || defaultCustomerUuid,
                  currentName:
                    editingRecord?.customerName || defaultCustomerName,
                }}
                request={async ({ keyWords, currentUuid, currentName }) =>
                  ensureCurrentOption(
                    await fetchCustomerOptions(keyWords),
                    form.getFieldValue('customerUuid'),
                    form.getFieldValue('customerUuid') === currentUuid
                      ? currentName
                      : undefined,
                  )
                }
                fieldProps={{
                  allowClear: true,
                  placeholder: '请选择客户（可留空）',
                  disabled: detailLoading || detailLoadFailed,
                  onChange: () => form.setFieldValue('contactUuid', undefined),
                }}
              />
            </Col>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormSelect
                name="contactUuid"
                label="关联联系人"
                showSearch
                debounceTime={300}
                params={{
                  customerUuid: selectedCustomerUuid,
                  currentUuid: editingRecord?.contactUuid,
                  currentName: editingRecord?.linkedContactName,
                }}
                request={async ({
                  keyWords,
                  customerUuid,
                  currentUuid,
                  currentName,
                }) =>
                  ensureCurrentOption(
                    await fetchContactOptions(keyWords, customerUuid),
                    form.getFieldValue('contactUuid'),
                    form.getFieldValue('contactUuid') === currentUuid
                      ? currentName
                      : undefined,
                  )
                }
                fieldProps={{
                  allowClear: true,
                  placeholder: '请选择联系人（可留空）',
                  disabled: detailLoading || detailLoadFailed,
                }}
              />
            </Col>
          </Row>

          <FormSectionDivider title="联系方式" />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormText name="contactName" label="联系人" />
            </Col>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormText
                name="mobile"
                label="手机"
                fieldProps={{ prefix: <MobileOutlined /> }}
              />
            </Col>
          </Row>
          <Row gutter={[12, 0]}>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormText name="telephone" label="电话" />
            </Col>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormText
                name="email"
                label="邮箱"
                fieldProps={{ prefix: <MailOutlined /> }}
              />
            </Col>
          </Row>
          <Row gutter={[12, 0]}>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormText name="qq" label="QQ" />
            </Col>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormText name="wechat" label="微信" />
            </Col>
          </Row>

          <FormSectionDivider title="跟进信息" />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormSelect
                name="followUpStatus"
                label="跟进状态"
                options={FOLLOW_UP_STATUS_OPTIONS}
                rules={[{ required: true, message: '请选择跟进状态' }]}
              />
            </Col>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormDatePicker
                name="contactLastTime"
                label="最近联系时间"
                fieldProps={{
                  style: compactFieldStyle,
                  placeholder: '选择日期',
                }}
              />
            </Col>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormDatePicker
                name="contactNextTime"
                label="下次联系时间"
                fieldProps={{
                  style: compactFieldStyle,
                  placeholder: '选择日期',
                }}
              />
            </Col>
          </Row>
          <ProFormTextArea
            name="contactLastContent"
            label="最近联系内容"
            fieldProps={{
              rows: 3,
              maxLength: 300,
              showCount: true,
              placeholder: '请输入最近一次联系的关键内容',
            }}
          />
          <ProFormText
            name="nextAction"
            label="下一步动作"
            fieldProps={{ placeholder: '例如：今天电话确认需求' }}
          />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="full" span={24}>
              <Form.Item name="areas" label="区域">
                <ResourceAreaSelector
                  options={areaOptions}
                  uuidPathMap={areaUuidPathMap}
                  disabled={relationLoading}
                />
              </Form.Item>
            </Col>
          </Row>
          <ProFormTextArea
            name="detailAddress"
            label="详细地址"
            fieldProps={{ rows: 2 }}
          />

          <FormSectionDivider title="资格" />
          <Row gutter={[12, 0]}>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormDigit
                name="leadAmount"
                label="线索金额"
                min={0}
                fieldProps={{
                  precision: 2,
                  style: compactFieldStyle,
                  addonBefore: '¥',
                }}
              />
            </Col>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormDigit
                name="employeeCount"
                label="员工数"
                min={0}
                fieldProps={{ precision: 0, style: compactFieldStyle }}
              />
            </Col>
          </Row>
          <Row gutter={[12, 0]}>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormDigit
                name="annualRevenue"
                label="年收入"
                min={0}
                fieldProps={{
                  precision: 2,
                  style: compactFieldStyle,
                  addonBefore: '¥',
                }}
              />
            </Col>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormSelect
                name="manualLeadGrade"
                label="人工评级"
                fieldProps={{
                  style: compactFieldStyle,
                  options: LEAD_GRADE_OPTIONS,
                  allowClear: true,
                  placeholder: '请选择（可留空）',
                }}
                extra="由销售人工判断，不影响系统自动评级。"
              />
            </Col>
          </Row>
          <Row gutter={[12, 0]}>
            <Col data-crm-field="short" xs={24} lg={12}>
              <ProFormSelect
                name="sourceChannel"
                label="来源渠道"
                fieldProps={{
                  style: compactFieldStyle,
                  showSearch: true,
                  filterOption: (input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                  optionFilterProp: 'label',
                  options: sourceChannelOptions,
                  loading: relationLoading,
                  allowClear: true,
                  placeholder: '请选择',
                }}
              />
            </Col>
            <Col data-crm-field="medium" xs={24} lg={12}>
              <ProFormSelect
                name="industryUuid"
                label="行业"
                fieldProps={{
                  style: compactFieldStyle,
                  showSearch: true,
                  filterOption: (input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase()),
                  optionFilterProp: 'label',
                  options: industryOptions,
                  loading: relationLoading,
                  allowClear: true,
                  placeholder: '请选择',
                }}
              />
            </Col>
          </Row>

          <FormSectionDivider title="介绍人关系" />
          <ReferralFormSection
            form={form}
            name="referrals"
            title=""
            initialItems={initialReferrals}
          />
        </div>
      </div>
    </ModalForm>
  );
}
